import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import AdminSalesLayout from '../../components/AdminSalesLayout';
import {
  listCourseCategories,
  createCourseCategory,
  updateCourseCategory,
  deleteCourseCategory,
} from '../../services/courseCategoryService';
import {
  listAssignedCourseCodes,
  listAvailableCourseCodes,
  assignCourseCode,
  unassignCourseCode,
} from '../../services/courseCodeCategoryService';

// ─── Styled components ────────────────────────────────────────────────────────

const PageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.section`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
`;

// Stats strip
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div`
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 18px 20px;
  background: ${(p) => p.$bg || '#f8fafc'};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #64748b;
`;

const StatValue = styled.span`
  font-size: 30px;
  font-weight: 800;
  color: #0f172a;
`;

// Toolbar
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  background: #f8fafc;
  transition: border-color 0.2s;
  &:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
    outline: none;
  }
`;

const StatusSelect = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  background: #f8fafc;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #f1f5f9; }
`;

// Table
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
`;

const Th = styled.th`
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #0f172a;
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:hover td { background: #f0fdf4; }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => (p.$active ? '#dcfce7' : '#f1f5f9')};
  color: ${(p) => (p.$active ? '#166534' : '#64748b')};
`;

const IconButton = styled.button`
  border: 1px solid ${(p) => p.$danger ? '#fecaca' : '#cbd5e1'};
  background: ${(p) => p.$danger ? '#fff1f2' : '#ffffff'};
  color: ${(p) => p.$danger ? '#b91c1c' : '#334155'};
  border-radius: 7px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: ${(p) => p.$danger ? '#fee2e2' : '#f1f5f9'};
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 0;
  color: #94a3b8;
  font-size: 15px;
`;

const ErrorBanner = styled.div`
  border: 1px solid #fecdd3;
  background: #fff1f2;
  color: #b91c1c;
  border-radius: 10px;
  padding: 14px 18px;
  font-size: 14px;
`;

// Pagination
const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
`;

const PaginationMeta = styled.span`
  font-size: 13px;
  color: #64748b;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 6px;
`;

const PagBtn = styled.button`
  border: 1px solid #cbd5e1;
  background: ${(p) => (p.$active ? '#10b981' : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#334155')};
  border-radius: 7px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:hover:not(:disabled):not([data-active='true']) { background: #f1f5f9; }
`;

// Modal
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 16px;
  z-index: 1200;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.35);
  border: 1px solid #e2e8f0;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  font-size: 22px;
  color: #94a3b8;
  cursor: pointer;
  line-height: 1;
  &:hover { color: #0f172a; }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
`;

const FieldRequired = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const TextInput = styled.input`
  border: 1px solid ${(p) => (p.$error ? '#ef4444' : '#cbd5e1')};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  background: #f8fafc;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus {
    border-color: ${(p) => (p.$error ? '#ef4444' : '#10b981')};
    box-shadow: 0 0 0 3px ${(p) => (p.$error ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)')};
    outline: none;
  }
`;

const TextArea = styled.textarea`
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  background: #f8fafc;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 88px;
  transition: border-color 0.2s;
  &:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
    outline: none;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Toggle = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #10b981;
`;

const FieldError = styled.span`
  font-size: 12px;
  color: #ef4444;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const SaveButton = styled(PrimaryButton)``;

// Assignment modal extras
const AssignModalCard = styled(ModalCard)`
  max-width: 680px;
`;

const AssignSearch = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 9px 13px;
  font-size: 14px;
  background: #f8fafc;
  margin-bottom: 12px;
  transition: border-color 0.2s;
  &:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
    outline: none;
  }
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

const PanelBox = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PanelCount = styled.span`
  background: #e2e8f0;
  color: #475569;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 700;
`;

const PanelList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 280px;
  overflow-y: auto;
  flex: 1;
`;

const PanelItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  color: #0f172a;
  cursor: pointer;
  transition: background 0.15s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${(p) => p.$assigned ? '#fff1f2' : '#f0fdf4'}; }
`;

const PanelItemText = styled.span`
  flex: 1;
  min-width: 0;
`;

const PanelItemSub = styled.span`
  font-size: 11px;
  color: #94a3b8;
  margin-left: 6px;
`;

const PanelActionBtn = styled.button`
  border: none;
  background: ${(p) => p.$remove ? '#fee2e2' : '#dcfce7'};
  color: ${(p) => p.$remove ? '#b91c1c' : '#166534'};
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.15s;
  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PanelEmpty = styled.div`
  padding: 28px 12px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const ROW_SIZE_OPTIONS = [50, 100, 200, 500, 1000];

const EMPTY_FORM = { name: '', description: '', isActive: true };

// ─── Component ────────────────────────────────────────────────────────────────

export default function CourseCategory() {
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pageSize: 50, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode, object = edit mode
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState({});
  const [saving, setSaving] = useState(false);

  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // ── assignment modal state ─────────────────────────────────────────────────
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // the category being managed
  const [assigned, setAssigned] = useState([]);           // assigned course codes
  const [available, setAvailable] = useState([]);         // available course codes
  const [assignSearch, setAssignSearch] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);    // courseCodeId being toggled

  const assignSearchRef = useRef(null);
  const assignDebounceRef = useRef(null);

  // ── fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async (currentPage = 1) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCourseCategories({
        search: search.trim() || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page: currentPage,
        pageSize,
      });
      setCategories(data.data || []);
      setPagination(data.pagination || { total: 0, page: currentPage, pageSize, totalPages: 1 });
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load categories.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, pageSize]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchData(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, statusFilter, fetchData]);

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ── modal helpers ──────────────────────────────────────────────────────────

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError({});
    setModalOpen(true);
  }

  function openEdit(cat) {
    setEditing(cat);
    setForm({ name: cat.name, description: cat.description || '', isActive: Boolean(cat.isActive) });
    setFormError({});
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
  }

  function handleFormChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (formError[name]) setFormError((prev) => ({ ...prev, [name]: null }));
  }

  function validateForm() {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required.';
    else if (form.name.trim().length > 255) errors.name = 'Name must be 255 characters or fewer.';
    return errors;
  }

  async function handleSave(e) {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length) {
      setFormError(errors);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        isActive: form.isActive,
      };
      if (editing) {
        await updateCourseCategory(editing.id, payload);
      } else {
        await createCourseCategory(payload);
      }
      closeModal();
      await fetchData(editing ? page : 1);
      if (!editing) setPage(1);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save. Please try again.';
      setFormError({ _global: msg });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(cat) {
    const result = await Swal.fire({
      title: 'Delete Category?',
      html: `<b>${cat.name}</b> will be permanently removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteCourseCategory(cat.id);
      await fetchData(page);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.error || 'Delete failed.', 'error');
    }
  }

  // ── assignment modal handlers ──────────────────────────────────────────────

  async function loadAssignmentData(cat, search = '') {
    setAssignLoading(true);
    setAssignError(null);
    try {
      const [assignedRes, availableRes] = await Promise.all([
        listAssignedCourseCodes(cat.id),
        listAvailableCourseCodes(cat.id, search),
      ]);
      setAssigned(assignedRes.data || []);
      setAvailable(availableRes.data || []);
    } catch (err) {
      setAssignError(err?.response?.data?.error || 'Failed to load course codes.');
    } finally {
      setAssignLoading(false);
    }
  }

  function openAssignModal(cat) {
    setAssignTarget(cat);
    setAssignSearch('');
    setAssignError(null);
    setAssigned([]);
    setAvailable([]);
    setAssignModalOpen(true);
    // defer so state is set before fetching
    setTimeout(() => loadAssignmentData(cat, ''), 0);
  }

  function closeAssignModal() {
    setAssignModalOpen(false);
    setAssignTarget(null);
    setAssignSearch('');
  }

  function handleAssignSearchChange(e) {
    const val = e.target.value;
    setAssignSearch(val);
    clearTimeout(assignDebounceRef.current);
    assignDebounceRef.current = setTimeout(() => {
      if (assignTarget) loadAssignmentData(assignTarget, val);
    }, 280);
  }

  async function handleAssign(courseCodeId) {
    if (!assignTarget || togglingId) return;
    setTogglingId(courseCodeId);
    setAssignError(null);
    try {
      await assignCourseCode(assignTarget.id, courseCodeId);
      await Promise.all([
        loadAssignmentData(assignTarget, assignSearch),
        fetchData(page),
      ]);
    } catch (err) {
      setAssignError(err?.response?.data?.error || 'Failed to assign course code.');
    } finally {
      setTogglingId(null);
    }
  }

  async function handleUnassign(courseCodeId) {
    if (!assignTarget || togglingId) return;
    setTogglingId(courseCodeId);
    setAssignError(null);
    try {
      await unassignCourseCode(assignTarget.id, courseCodeId);
      await Promise.all([
        loadAssignmentData(assignTarget, assignSearch),
        fetchData(page),
      ]);
    } catch (err) {
      setAssignError(err?.response?.data?.error || 'Failed to remove assignment.');
    } finally {
      setTogglingId(null);
    }
  }

  // ── derived stats ──────────────────────────────────────────────────────────

  const totalActive   = categories.filter((c) => c.isActive).length;
  const totalInactive = categories.filter((c) => !c.isActive).length;

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <AdminSalesLayout title="Course Category" currentSection="branchCourseCategory">
      <PageSection>

        {/* Stats */}
        <StatsRow>
          <StatCard $bg="#f0fdf4">
            <StatLabel>Total</StatLabel>
            <StatValue>{pagination.total}</StatValue>
          </StatCard>
          <StatCard $bg="#ecfdf5">
            <StatLabel>Active</StatLabel>
            <StatValue style={{ color: '#059669' }}>{totalActive}</StatValue>
          </StatCard>
          <StatCard $bg="#fff7ed">
            <StatLabel>Inactive</StatLabel>
            <StatValue style={{ color: '#b45309' }}>{totalInactive}</StatValue>
          </StatCard>
        </StatsRow>

        {/* Main card */}
        <Card>
          {/* Toolbar */}
          <Toolbar style={{ marginBottom: '16px' }}>
            <SearchInput
              ref={searchRef}
              type="text"
              placeholder="Search by name or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <StatusSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </StatusSelect>
            <StatusSelect
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              title="Rows per page"
            >
              {ROW_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n} rows</option>
              ))}
            </StatusSelect>
            <PrimaryButton type="button" onClick={openCreate}>
              + Add Category
            </PrimaryButton>
          </Toolbar>

          {/* Error */}
          {error && <ErrorBanner style={{ marginBottom: '16px' }}>{error}</ErrorBanner>}

          {/* Table */}
          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Name</Th>
                  <Th>Description</Th>
                  <Th>Course Codes</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                  <Th style={{ textAlign: 'right' }}>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <Td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>
                      Loading…
                    </Td>
                  </tr>
                ) : categories.length === 0 ? (
                  <tr>
                    <Td colSpan={7}>
                      <EmptyState>
                        {search || statusFilter !== 'all'
                          ? 'No categories match your search.'
                          : 'No categories yet. Click "+ Add Category" to create the first one.'}
                      </EmptyState>
                    </Td>
                  </tr>
                ) : (
                  categories.map((cat, idx) => (
                    <Tr key={cat.id}>
                      <Td style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums', width: '48px' }}>
                        {(page - 1) * pageSize + idx + 1}
                      </Td>
                      <Td style={{ fontWeight: 600 }}>{cat.name}</Td>
                      <Td style={{ color: '#475569', maxWidth: '320px' }}>
                        {cat.description
                          ? cat.description.length > 120
                            ? cat.description.slice(0, 120) + '…'
                            : cat.description
                          : <span style={{ color: '#cbd5e1' }}>—</span>
                        }
                      </Td>
                      <Td>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            background: cat.courseCodeCount > 0 ? '#eff6ff' : '#f8fafc',
                            color: cat.courseCodeCount > 0 ? '#1d4ed8' : '#94a3b8',
                            border: `1px solid ${cat.courseCodeCount > 0 ? '#bfdbfe' : '#e2e8f0'}`,
                            borderRadius: '999px',
                            padding: '3px 10px',
                            fontSize: '13px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                          title="Open assignment panel"
                          onClick={() => openAssignModal(cat)}
                        >
                          {cat.courseCodeCount ?? 0}
                        </span>
                      </Td>
                      <Td>
                        <Badge $active={cat.isActive}>{cat.isActive ? 'Active' : 'Inactive'}</Badge>
                      </Td>
                      <Td style={{ color: '#64748b', fontSize: '13px' }}>
                        {cat.createdAt ? new Date(cat.createdAt).toLocaleDateString() : '—'}
                      </Td>
                      <Td>
                        <ActionGroup style={{ justifyContent: 'flex-end' }}>
                          <IconButton type="button" style={{ borderColor: '#bfdbfe', background: '#eff6ff', color: '#1d4ed8' }} onClick={() => openAssignModal(cat)}>
                            Assign Codes
                          </IconButton>
                          <IconButton type="button" onClick={() => openEdit(cat)}>Edit</IconButton>
                          <IconButton type="button" $danger onClick={() => handleDelete(cat)}>Delete</IconButton>
                        </ActionGroup>
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </StyledTable>
          </TableWrapper>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Pagination>
              <PaginationMeta>
                {Math.min((page - 1) * pageSize + 1, pagination.total)}–
                {Math.min(page * pageSize, pagination.total)} of {pagination.total}
              </PaginationMeta>
              <PaginationButtons>
                <PagBtn disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</PagBtn>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter((p) => Math.abs(p - page) <= 2)
                  .map((p) => (
                    <PagBtn key={p} $active={p === page} data-active={p === page} onClick={() => setPage(p)}>
                      {p}
                    </PagBtn>
                  ))}
                <PagBtn disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>Next →</PagBtn>
              </PaginationButtons>
            </Pagination>
          )}
        </Card>

      </PageSection>

      {/* ── Modal ────────────────────────────────────────────────────────────── */}
      {modalOpen && (
        <Overlay onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <ModalCard>
            <ModalHeader>
              <ModalTitle>{editing ? 'Edit Category' : 'Add Category'}</ModalTitle>
              <CloseBtn type="button" onClick={closeModal} aria-label="Close">×</CloseBtn>
            </ModalHeader>

            <form onSubmit={handleSave} noValidate>
              {formError._global && (
                <ErrorBanner style={{ marginBottom: '16px' }}>{formError._global}</ErrorBanner>
              )}

              <FieldGroup>
                <FieldLabel htmlFor="cat-name">
                  Name <FieldRequired>*</FieldRequired>
                </FieldLabel>
                <TextInput
                  id="cat-name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleFormChange}
                  $error={Boolean(formError.name)}
                  placeholder="e.g. HND Computing"
                  maxLength={255}
                  autoFocus
                />
                {formError.name && <FieldError>{formError.name}</FieldError>}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="cat-description">Description</FieldLabel>
                <TextArea
                  id="cat-description"
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  placeholder="Optional short description…"
                  maxLength={2000}
                />
              </FieldGroup>

              <FieldGroup>
                <ToggleRow>
                  <Toggle
                    id="cat-active"
                    name="isActive"
                    type="checkbox"
                    checked={form.isActive}
                    onChange={handleFormChange}
                  />
                  <FieldLabel htmlFor="cat-active" style={{ margin: 0, cursor: 'pointer' }}>
                    Active
                  </FieldLabel>
                </ToggleRow>
              </FieldGroup>

              <ModalActions>
                <SecondaryButton type="button" onClick={closeModal}>Cancel</SecondaryButton>
                <SaveButton type="submit" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create'}
                </SaveButton>
              </ModalActions>
            </form>
          </ModalCard>
        </Overlay>
      )}
      {/* ── Assign Course Codes Modal ──────────────────────────────────────── */}
      {assignModalOpen && assignTarget && (
        <Overlay onClick={(e) => e.target === e.currentTarget && closeAssignModal()}>
          <AssignModalCard>
            <ModalHeader>
              <div>
                <ModalTitle>Assign Course Codes</ModalTitle>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                  Category: <strong>{assignTarget.name}</strong>
                </div>
              </div>
              <CloseBtn type="button" onClick={closeAssignModal} aria-label="Close">×</CloseBtn>
            </ModalHeader>

            {assignError && (
              <ErrorBanner style={{ marginBottom: '14px' }}>{assignError}</ErrorBanner>
            )}

            <AssignSearch
              ref={assignSearchRef}
              type="text"
              placeholder="Search available course codes…"
              value={assignSearch}
              onChange={handleAssignSearchChange}
              autoFocus
            />

            {assignLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '14px' }}>
                Loading…
              </div>
            ) : (
              <TwoCols>
                {/* Assigned */}
                <PanelBox>
                  <PanelHeader>
                    Assigned <PanelCount>{assigned.length}</PanelCount>
                  </PanelHeader>
                  <PanelList>
                    {assigned.length === 0 ? (
                      <PanelEmpty>No course codes assigned yet.</PanelEmpty>
                    ) : (
                      assigned.map((item) => (
                        <PanelItem key={item.courseCodeId} $assigned>
                          <PanelItemText>
                            {item.fullName}
                            {item.shortName && (
                              <PanelItemSub>({item.shortName})</PanelItemSub>
                            )}
                          </PanelItemText>
                          <PanelActionBtn
                            $remove
                            disabled={togglingId === item.courseCodeId}
                            onClick={() => handleUnassign(item.courseCodeId)}
                            title="Remove assignment"
                          >
                            {togglingId === item.courseCodeId ? '…' : '✕ Remove'}
                          </PanelActionBtn>
                        </PanelItem>
                      ))
                    )}
                  </PanelList>
                </PanelBox>

                {/* Available */}
                <PanelBox>
                  <PanelHeader>
                    Available <PanelCount>{available.length}</PanelCount>
                  </PanelHeader>
                  <PanelList>
                    {available.length === 0 ? (
                      <PanelEmpty>
                        {assignSearch
                          ? 'No results for your search.'
                          : 'All course codes are already assigned.'}
                      </PanelEmpty>
                    ) : (
                      available.map((item) => (
                        <PanelItem key={item.courseCodeId}>
                          <PanelItemText>
                            {item.fullName}
                            {item.shortName && (
                              <PanelItemSub>({item.shortName})</PanelItemSub>
                            )}
                          </PanelItemText>
                          <PanelActionBtn
                            disabled={togglingId === item.courseCodeId}
                            onClick={() => handleAssign(item.courseCodeId)}
                            title="Assign to category"
                          >
                            {togglingId === item.courseCodeId ? '…' : '+ Assign'}
                          </PanelActionBtn>
                        </PanelItem>
                      ))
                    )}
                  </PanelList>
                </PanelBox>
              </TwoCols>
            )}

            <ModalActions style={{ marginTop: '20px' }}>
              <SecondaryButton type="button" onClick={closeAssignModal}>Done</SecondaryButton>
            </ModalActions>
          </AssignModalCard>
        </Overlay>
      )}

    </AdminSalesLayout>
  );
}
